import React from "react";
import { MailPerformanceExplorer } from "../../components/directmail/MailPerformanceExplorer";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Sheet,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Envelope as BatchIcon,
  MapPin as MapIcon,
  Megaphone as CampaignIcon,
  Ruler as QueueIcon,
} from "@phosphor-icons/react";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../components/common/ResponsiveTable";
import {
  formatMoney,
  getDirectMailStats,
  moneyToNumber,
} from "../../lib/directMailApi";
import { getApiErrorMessage } from "../../lib/apiError";
import {
  MAIL_TEMPLATE_LABELS,
  type DirectMailCampaignStatsRow,
  type DirectMailStats,
  type MailTemplateKind,
  type MoneyString,
} from "../../types/directMail";
import {
  DirectMailPageHeader,
  DIRECT_MAIL_ROUTES,
} from "../../components/directmail/DirectMailNav";
import { DirectMailStatTile } from "../../components/directmail/DirectMailStatTile";

/** The stats view splits spend between the two things Direct Mail can charge for. */
type DirectMailStatsResponse = DirectMailStats & {
  measurement_spend?: MoneyString | number;
  mail_spend?: MoneyString | number;
};

/** The per-campaign rows come from a values() query, so the key is `id`. */
type CampaignStatsRow = DirectMailCampaignStatsRow & {
  id?: number;
  status?: string;
  template_kind?: MailTemplateKind;
};

const QUICK_LINKS = [
  {
    to: DIRECT_MAIL_ROUTES.map,
    label: "Pin houses",
    body: "Drop pins on the map, or let reps pin from the truck.",
    icon: <MapIcon />,
  },
  {
    to: DIRECT_MAIL_ROUTES.queue,
    label: "Measure the queue",
    body: "Claim pins, measure roofs, approve the prices.",
    icon: <QueueIcon />,
  },
  {
    to: DIRECT_MAIL_ROUTES.campaigns,
    label: "Campaigns",
    body: "Piece type, pricing tier and the copy that gets printed.",
    icon: <CampaignIcon />,
  },
  {
    to: DIRECT_MAIL_ROUTES.batches,
    label: "Batches",
    body: "Group approved pins, read the proof, mail it.",
    icon: <BatchIcon />,
  },
];

export const DirectMailDashboardPage: React.FC = () => {
  const statsQuery = useQuery({
    queryKey: ["direct-mail", "stats"],
    queryFn: () => getDirectMailStats() as Promise<DirectMailStatsResponse>,
  });

  const stats = statsQuery.data;
  const loading = statsQuery.isLoading;
  const campaigns = (stats?.by_campaign ?? []) as CampaignStatsRow[];
  const nothingYet = !loading && stats !== undefined && stats.pinned === 0;

  const funnel = [
    { name: "Pinned", value: stats?.pinned ?? 0 },
    { name: "Measured", value: stats?.measured ?? 0 },
    { name: "Mailed", value: stats?.mailed ?? 0 },
    { name: "Scans", value: stats?.scans ?? 0 },
    { name: "Leads", value: stats?.leads ?? 0 },
    { name: "Jobs", value: stats?.jobs ?? 0 },
  ];

  const columns: ResponsiveColumn<CampaignStatsRow>[] = [
    {
      key: "name",
      sortValue: (row) => row.name,
      header: "Campaign",
      width: "24%",
      mobileVariant: "header",
      render: (row) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography level="title-sm">{row.name}</Typography>
          {row.template_kind && (
            <Chip size="sm" variant="soft">
              {MAIL_TEMPLATE_LABELS[row.template_kind]}
            </Chip>
          )}
        </Stack>
      ),
    },
    {
      key: "pinned",
      sortValue: (row) => row.pinned,
      header: "Pinned",
      width: "9%",
      render: (row) => <Typography level="body-sm">{row.pinned}</Typography>,
    },
    {
      key: "measured",
      sortValue: (row) => row.measured,
      header: "Measured",
      width: "9%",
      render: (row) => <Typography level="body-sm">{row.measured}</Typography>,
    },
    {
      key: "mailed",
      sortValue: (row) => row.mailed,
      header: "Mailed",
      width: "9%",
      render: (row) => <Typography level="body-sm">{row.mailed}</Typography>,
    },
    {
      key: "scans",
      sortValue: (row) => row.scans,
      header: "Scans",
      width: "9%",
      render: (row) => <Typography level="body-sm">{row.scans}</Typography>,
    },
    {
      key: "leads",
      sortValue: (row) => row.leads,
      header: "Leads",
      width: "9%",
      render: (row) => <Typography level="body-sm">{row.leads}</Typography>,
    },
    {
      key: "spend",
      header: "Spend",
      width: "11%",
      render: (row) => (
        <Typography level="body-sm">
          {formatMoney(row.spend, { decimals: 0 })}
        </Typography>
      ),
    },
    {
      key: "cpl",
      sortValue: (row) => Number(row.cost_per_lead ?? 0),
      header: "Cost / lead",
      width: "12%",
      render: (row) => (
        <Typography level="body-sm">
          {row.cost_per_lead === null || row.cost_per_lead === undefined
            ? "—"
            : formatMoney(row.cost_per_lead)}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <DirectMailPageHeader
        title="Direct Mail"
        description="Pin a house, measure the roof, mail a priced estimate — and see what came back."
      />

      {statsQuery.isError && (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
          <Box>
            <Typography level="title-sm">
              The funnel could not be loaded
            </Typography>
            <Typography level="body-sm">
              {getApiErrorMessage(
                statsQuery.error,
                "Please try again in a moment.",
              )}
            </Typography>
            <Button
              size="sm"
              variant="plain"
              sx={{ px: 0, mt: 0.5 }}
              onClick={() => void statsQuery.refetch()}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      )}

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Pinned"
            value={stats?.pinned ?? 0}
            loading={loading}
            hint="Houses targeted"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Measured"
            value={stats?.measured ?? 0}
            loading={loading}
            hint="Roofs priced"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Mailed"
            value={stats?.mailed ?? 0}
            loading={loading}
            hint="Pieces in the mail"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="QR scans"
            value={stats?.scans ?? 0}
            loading={loading}
            hint="Landing page visits"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Leads"
            value={stats?.leads ?? 0}
            loading={loading}
            hint="Homeowners who claimed a quote"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Jobs"
            value={stats?.jobs ?? 0}
            loading={loading}
            hint="Work won from those leads"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Spend"
            value={formatMoney(stats?.spend, { decimals: 0 })}
            loading={loading}
            hint="Printing, postage and measurements"
          />
        </Grid>
        <Grid xs={6} sm={4} md={3}>
          <DirectMailStatTile
            label="Cost per lead"
            value={
              stats?.cost_per_lead === null ||
              stats?.cost_per_lead === undefined
                ? "—"
                : formatMoney(stats.cost_per_lead)
            }
            loading={loading}
            hint={stats?.leads ? `${stats.leads} leads so far` : "No leads yet"}
          />
        </Grid>
      </Grid>

      {nothingYet ? (
        <Sheet
          variant="soft"
          sx={{ p: 4, borderRadius: "sm", textAlign: "center", mb: 2 }}
        >
          <Typography level="title-md">Nothing pinned yet</Typography>
          <Typography
            level="body-sm"
            sx={{ color: "text.tertiary", mt: 1, mb: 2 }}
          >
            Direct mail starts with a house. Create a campaign so pins have
            somewhere to go, then pin the neighborhoods you want to own.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="center"
          >
            <Button
              component={RouterLink}
              to={DIRECT_MAIL_ROUTES.campaigns}
              startDecorator={<CampaignIcon />}
            >
              Create a campaign
            </Button>
            <Button
              component={RouterLink}
              to={DIRECT_MAIL_ROUTES.map}
              variant="outlined"
              color="neutral"
              startDecorator={<MapIcon />}
            >
              Pin houses on the map
            </Button>
          </Stack>
        </Sheet>
      ) : (
        <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            Funnel
          </Typography>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={260}
              sx={{ borderRadius: "sm" }}
            />
          ) : (
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip />
                  <Bar dataKey="value" fill="#0084ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
          {(stats?.mail_spend !== undefined ||
            stats?.measurement_spend !== undefined) && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Box>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    Printing and postage
                  </Typography>
                  <Typography level="title-sm">
                    {formatMoney(stats?.mail_spend)}
                  </Typography>
                </Box>
                <Box>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    ApexOS measurements
                  </Typography>
                  <Typography level="title-sm">
                    {formatMoney(stats?.measurement_spend)}
                  </Typography>
                </Box>
                <Box>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    Revenue-bearing jobs
                  </Typography>
                  <Typography level="title-sm">
                    {stats?.jobs ?? 0} from{" "}
                    {moneyToNumber(stats?.spend) > 0
                      ? formatMoney(stats?.spend, { decimals: 0 })
                      : "$0"}
                  </Typography>
                </Box>
              </Stack>
            </>
          )}
        </Card>
      )}

      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>
          By campaign
        </Typography>
        {loading ? (
          <Stack spacing={1}>
            {[0, 1, 2].map((row) => (
              <Skeleton
                key={row}
                variant="rectangular"
                height={44}
                sx={{ borderRadius: "sm" }}
              />
            ))}
          </Stack>
        ) : (
          <ResponsiveTable<CampaignStatsRow>
            rows={campaigns}
            getRowKey={(row) => row.id ?? row.campaign}
            columns={columns}
            emptyState={
              <Sheet
                variant="soft"
                sx={{ p: 3, borderRadius: "sm", textAlign: "center" }}
              >
                <Typography level="title-sm">No campaigns yet</Typography>
                <Typography
                  level="body-sm"
                  sx={{ color: "text.tertiary", mt: 0.5, mb: 1.5 }}
                >
                  A campaign is what ties a pin to a piece, a price and a QR
                  code.
                </Typography>
                <Button
                  component={RouterLink}
                  to={DIRECT_MAIL_ROUTES.campaigns}
                  size="sm"
                >
                  Create a campaign
                </Button>
              </Sheet>
            }
          />
        )}
      </Card>

      <Grid container spacing={1}>
        {QUICK_LINKS.map((link) => (
          <Grid key={link.to} xs={12} sm={6} md={3}>
            <Card
              variant="soft"
              component={RouterLink}
              to={link.to}
              sx={{
                p: 2,
                height: "100%",
                textDecoration: "none",
                "&:hover": { boxShadow: "sm" },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {link.icon}
                <Typography level="title-sm">{link.label}</Typography>
              </Stack>
              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                {link.body}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
      <MailPerformanceExplorer />
    </Box>
  );
};
